/**
 * ServiceAddressPicker
 *
 * Lets the office choose WHICH address a CustomerService lives at — either by
 * picking from the customer's saved address book, or by entering a new one.
 *
 * Why this exists: a single customer can have services at two different sites
 * (e.g. Starlink at the holiday house, NBN at the home). Each service needs its
 * own address, but until now there was no UI to set it — Add Service only had a
 * single free-text box and Edit Service had no address field at all.
 *
 * Design:
 *  - The customer's `addresses[]` array (+ the legacy single-address field) is
 *    the registry of known addresses. This picker reads from it.
 *  - When a new address is entered, it offers to save it back to the customer's
 *    book (dedup-safe via addCustomerAddress) so it's reusable for the next
 *    service and shows up consistently everywhere.
 *  - The service itself keeps storing denormalised address/city/state/zipCode
 *    strings (the existing CustomerService schema) — there is no addressId
 *    reference, so nothing downstream (cards, transfers, getServicesByAddress)
 *    has to change.
 *
 * Styling matches the dark-gradient Add/Edit Service modals it lives in.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Plus, Check, Star, Loader2, ChevronLeft } from 'lucide-react';
import { Customer, CustomerAddress } from '@/types';
import {
  getCustomer,
  addCustomerAddress,
  normalizeAddress,
  buildCustomerAddressBook,
} from '@/services/customer.service';
import { AddressAutocomplete } from '@/components/deals/AddressAutocomplete';
import { AddressTagChips } from '@/components/customers/AddressTagChips';

export interface ServiceAddressSelection {
  address: string;
  city: string;
  state: string;
  zipCode: string; // postcode — named zipCode to match the CustomerService schema
  /** When true, a newly-entered address is persisted to the customer's address
   *  book by the parent on save (via commitServiceAddressToBook). */
  saveToBook: boolean;
}

export function emptyServiceAddress(): ServiceAddressSelection {
  return { address: '', city: '', state: '', zipCode: '', saveToBook: false };
}

/** Build a selection object from an existing service's stored fields. */
export function serviceAddressFromFields(fields: {
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): ServiceAddressSelection {
  return {
    address: fields.address || '',
    city: fields.city || '',
    state: fields.state || '',
    zipCode: fields.zipCode || '',
    saveToBook: false,
  };
}

/**
 * Persist a newly-entered service address to the customer's address book.
 * Called by the parent modal AFTER its own save succeeds. No-op when the user
 * didn't opt in or there's nothing to save. addCustomerAddress dedups by
 * normalized address string, so this is safe to call unconditionally.
 */
export async function commitServiceAddressToBook(
  customerId: string,
  sel: ServiceAddressSelection,
  labelHint?: string,
): Promise<void> {
  if (!sel.saveToBook || !sel.address.trim() || !customerId) return;
  try {
    await addCustomerAddress(customerId, {
      label: labelHint?.trim() || 'Service',
      address: sel.address.trim(),
      city: sel.city.trim() || undefined,
      state: sel.state.trim() || undefined,
      postcode: sel.zipCode.trim() || undefined,
      country: 'Australia',
      isPrimary: false,
      source: 'manual',
    });
  } catch (err) {
    // Best-effort — the service write is the source of truth; a failed book
    // update shouldn't surface as a service-save failure.
    console.error('[ServiceAddressPicker] Failed to save address to customer book:', err);
  }
}

interface Props {
  customerId: string;
  value: ServiceAddressSelection;
  onChange: (value: ServiceAddressSelection) => void;
  /** Shown beneath the picker — e.g. "Shared across every service in this bundle". */
  hint?: string;
  /**
   * When there's no incoming address, pre-select the customer's primary saved
   * address. Use for the Add flow (new services default to the primary site).
   * Leave off for Edit so we never invent an address a service didn't have.
   */
  autoSelectPrimary?: boolean;
}

export function ServiceAddressPicker({ customerId, value, onChange, hint, autoSelectPrimary }: Props) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState<'saved' | 'custom'>('saved');
  const initialisedFor = useRef<string | null>(null);

  // One-time fetch of the customer (for the saved-address book).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    initialisedFor.current = null;
    getCustomer(customerId)
      .then((c) => { if (!cancelled) setCustomer(c); })
      .catch((err) => console.error('[ServiceAddressPicker] Failed to load customer:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId]);

  const saved = useMemo(() => buildCustomerAddressBook(customer), [customer]);

  const addressInBook = (addr: string) =>
    !!addr.trim() && saved.some((a) => normalizeAddress(a.address) === normalizeAddress(addr));

  // Pick the initial mode once the customer (and therefore the book) has loaded.
  useEffect(() => {
    if (loading) return;
    if (initialisedFor.current === customerId) return;
    initialisedFor.current = customerId;

    if (addressInBook(value.address)) {
      setMode('saved');
    } else if (value.address.trim()) {
      // Pre-existing free-text address that isn't in the book yet — show it in
      // custom mode and default to saving it so the book gets tidied up.
      setMode('custom');
      if (!value.saveToBook) onChange({ ...value, saveToBook: true });
    } else if (saved.length > 0) {
      setMode('saved');
      // Add flow: default a brand-new service to the customer's primary site.
      if (autoSelectPrimary) {
        const primary = saved.find((a) => a.isPrimary) || saved[0];
        onChange({
          address: primary.address || '',
          city: primary.city || '',
          state: primary.state || '',
          zipCode: primary.postcode || '',
          saveToBook: false,
        });
      }
    } else {
      setMode('custom');
    }
     
  }, [loading, customerId, saved]);

  const selectedSavedId = useMemo(() => {
    if (!value.address.trim()) return null;
    return saved.find((a) => normalizeAddress(a.address) === normalizeAddress(value.address))?.id ?? null;
  }, [saved, value.address]);

  const pickSaved = (a: CustomerAddress) => {
    onChange({
      address: a.address || '',
      city: a.city || '',
      state: a.state || '',
      zipCode: a.postcode || '',
      saveToBook: false,
    });
  };

  const enterCustomFresh = () => {
    setMode('custom');
    onChange({ address: '', city: '', state: '', zipCode: '', saveToBook: true });
  };

  // Custom-mode field handlers — picking/typing a NEW address flips saveToBook
  // on automatically (unless it's one already in the book).
  const handleStreetChange = (text: string) => {
    onChange({ ...value, address: text, saveToBook: text.trim() ? !addressInBook(text) : false });
  };
  const handleAutocompleteSelect = (r: { fullAddress: string; city: string; state: string; postcode: string }) => {
    onChange({
      address: r.fullAddress,
      city: r.city,
      state: r.state,
      zipCode: r.postcode,
      saveToBook: !addressInBook(r.fullAddress),
    });
  };

  const inputClass =
    'w-full px-3 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors text-sm';

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading addresses…
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {mode === 'saved' ? (
        <>
          {saved.length > 0 ? (
            <div className="space-y-2">
              {saved.map((a) => {
                const isSel = a.id === selectedSavedId;
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => pickSaved(a)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border transition-all flex items-start gap-3 ${
                      isSel
                        ? 'bg-brand-secondary/20 border-brand-secondary/50'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isSel ? 'text-brand-secondary' : 'text-slate-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold text-white">{a.label}</span>
                        {a.isPrimary && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                            <Star className="w-2.5 h-2.5" /> Main
                          </span>
                        )}
                        {/* Holiday / investment / "brother's place" — the whole
                            reason this picker exists is that one customer has
                            services at several kinds of site. */}
                        <AddressTagChips tags={a.tags} />
                        {a.source === 'service' && a.sourceServiceType && (
                          <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                            From: {a.sourceServiceType}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-300 truncate">{a.address}</div>
                      {(a.city || a.state || a.postcode) && (
                        <div className="text-[11px] text-slate-500 truncate">
                          {[a.city, a.state, a.postcode].filter(Boolean).join(' ')}
                        </div>
                      )}
                    </div>
                    {isSel && <Check className="w-4 h-4 text-brand-secondary flex-shrink-0 mt-0.5" />}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-sm text-slate-400 py-2">No saved addresses for this customer yet.</div>
          )}

          <button
            type="button"
            onClick={enterCustomFresh}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Use a different address
          </button>
        </>
      ) : (
        <>
          {saved.length > 0 && (
            <button
              type="button"
              onClick={() => setMode('saved')}
              className="flex items-center gap-1 text-xs font-medium text-brand-secondary hover:text-brand-secondary/80 transition-colors"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Back to saved addresses
            </button>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">Street address</label>
            <AddressAutocomplete
              value={value.address}
              onChange={handleStreetChange}
              onAddressSelect={handleAutocompleteSelect}
              placeholder="Start typing the service address…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">City / Suburb</label>
              <input
                type="text"
                value={value.city}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, city: e.target.value })}
                placeholder="e.g. Wollongong"
                className={inputClass}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">State</label>
                <input
                  type="text"
                  value={value.state}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, state: e.target.value })}
                  placeholder="NSW"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Postcode</label>
                <input
                  type="text"
                  value={value.zipCode}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, zipCode: e.target.value })}
                  placeholder="2500"
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Save-to-book opt-out — only meaningful when the address isn't already known */}
          {value.address.trim() && !addressInBook(value.address) && (
            <label className="flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={value.saveToBook}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, saveToBook: e.target.checked })}
                className="w-4 h-4 mt-0.5 rounded border-white/20 bg-white/10 accent-brand-secondary"
              />
              <span className="text-xs text-slate-300">
                Save this address to the customer's saved addresses
                <span className="text-slate-500"> — so you can reuse it for their next service.</span>
              </span>
            </label>
          )}
        </>
      )}

      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}
